import { FC, ReactNode, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  DocumentTextIcon,
  FolderIcon,
  HomeIcon,
  NewspaperIcon,
  PhotoIcon,
  Squares2X2Icon,
  UserGroupIcon,
  BuildingOffice2Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const navItems = [
  { to: "/admin", label: "Dashboard", icon: HomeIcon, end: true },
  { to: "/admin/categories", label: "Categories", icon: FolderIcon },
  { to: "/admin/products", label: "Products", icon: Squares2X2Icon },
  { to: "/admin/blogs", label: "Blogs", icon: NewspaperIcon },
  { to: "/admin/clients", label: "Clients", icon: UserGroupIcon },
  { to: "/admin/projects", label: "Projects", icon: BuildingOffice2Icon },
  { to: "/admin/downloads", label: "Downloads", icon: DocumentTextIcon },
];

const AdminLayout: FC<{ children?: ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const Sidebar = (
    <aside className="flex flex-col h-full bg-[#0f1b3d] text-white">
      <div className="px-5 py-6 border-b border-white/10">
        <Link to="/admin" className="block">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-primary-300">
            BCR Admin
          </p>
          <p className="mt-1 text-lg font-bold">Content Portal</p>
        </Link>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                isActive
                  ? "bg-white/15 text-white"
                  : "text-white/70 hover:bg-white/10 hover:text-white"
              }`
            }
          >
            <item.icon className="w-5 h-5 shrink-0" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-4 py-4 border-t border-white/10">
        <p className="text-xs text-white/50 truncate">{user?.username}</p>
        <button
          type="button"
          onClick={handleLogout}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 h-10 rounded-xl bg-white/10 hover:bg-white/15 text-sm font-semibold"
        >
          <ArrowRightOnRectangleIcon className="w-4 h-4" />
          Sign out
        </button>
        <Link
          to="/"
          className="mt-2 block text-center text-xs text-white/40 hover:text-white/70"
        >
          ← Back to website
        </Link>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-neutral-950 flex">
      <div className="hidden lg:block w-64 shrink-0 fixed inset-y-0 left-0 z-30">
        {Sidebar}
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="w-64">{Sidebar}</div>
          <button
            type="button"
            className="flex-1 bg-black/50"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          />
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="sticky top-0 z-20 bg-white/90 dark:bg-neutral-900/90 backdrop-blur border-b border-neutral-200 dark:border-neutral-800 px-4 sm:px-6 h-14 flex items-center justify-between">
          <button
            type="button"
            className="lg:hidden p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800"
            onClick={() => setOpen(true)}
          >
            {open ? (
              <XMarkIcon className="w-5 h-5" />
            ) : (
              <Bars3Icon className="w-5 h-5" />
            )}
          </button>
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <PhotoIcon className="w-4 h-4" />
            Manage website content
          </div>
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {user?.first_name || user?.username}
          </span>
        </header>
        <main className="p-4 sm:p-6 lg:p-8">{children || <Outlet />}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
